import React, { useState, useMemo, useRef, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { IconPlus } from '../shared/icons';

/**
 * ShowcaseCarouselList — Welcome Showcase carousel list for admin users.
 *
 * Follows the same visual patterns as CarouselList but adds:
 * - Published state indicator (amber LED vs green)
 * - Publish / Unpublish buttons
 * - Delete blocked for published carousels
 *
 * Sits between the Carousels section and Normies Studio in the editor.
 */
export function ShowcaseCarouselList({
  showcases,
  activeId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  onPublish,
  onUnpublish,
  isCreating = false,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const [publishingId, setPublishingId] = useState(null);

  const containerRef = useRef(null);

  const sorted = useMemo(
    () => [...showcases].sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')),
    [showcases],
  );

  const handleStartRename = (carousel, e) => {
    e.stopPropagation();
    setPendingDeleteId(null);
    setEditingId(carousel.id);
    setEditName(carousel.name);
  };

  const handleFinishRename = () => {
    if (editingId && editName.trim()) {
      onRename(editingId, editName.trim());
    }
    setEditingId(null);
    setEditName('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleFinishRename();
    else if (e.key === 'Escape') { setEditingId(null); setEditName(''); }
  };

  const isPublished = (carousel) => carousel._showcase?.published === true;

  const handleDeleteClick = useCallback((id, e) => {
    e.stopPropagation();
    const carousel = showcases.find(c => c.id === id);
    if (isPublished(carousel)) return; // Can't delete published

    const badge = e.currentTarget;
    const btn = badge.closest('.carousel-item');
    const container = containerRef.current;

    if (btn && container) {
      const containerRect = container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const popupW = 280;
      let left = (btnRect.left + btnRect.width / 2) - containerRect.left - popupW / 2;
      const maxLeft = containerRect.width - popupW;
      left = Math.max(0, Math.min(left, maxLeft));
      setPopupPos({ top: btnRect.bottom - containerRect.top + 4, left });
    }
    setPendingDeleteId(id);
  }, [showcases]);

  const handleDeleteConfirm = () => {
    if (pendingDeleteId) onDelete(pendingDeleteId);
    setPendingDeleteId(null);
  };

  const handleDeleteCancel = (e) => {
    if (e) e.stopPropagation();
    setPendingDeleteId(null);
  };

  const handlePublishToggle = async (carousel, e) => {
    e.stopPropagation();
    if (publishingId) return; // debounce
    setPublishingId(carousel.id);
    try {
      if (isPublished(carousel)) {
        await onUnpublish(carousel.id);
      } else {
        await onPublish(carousel.id);
      }
    } finally {
      setPublishingId(null);
    }
  };

  if (showcases.length === 0 && !isCreating) {
    return (
      <div className="relative" ref={containerRef}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-xs text-white/50 uppercase tracking-wider">Welcome Showcase</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onCreate}
            disabled={isCreating}
            className="carousel-item carousel-item-inactive"
            title="Create first showcase"
            style={{ gap: '4px' }}
          >
            <IconPlus className="h-3 w-3" style={{ opacity: 0.5 }} />
            <span>New Showcase</span>
          </button>
        </div>
        <div className="mt-1.5 text-[10px] text-white/30">
          Create a demo carousel that visitors see by default
        </div>
      </div>
    );
  }

  const pendingCarousel = pendingDeleteId ? showcases.find(c => c.id === pendingDeleteId) : null;

  return (
    <div className="relative" ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs text-white/50 uppercase tracking-wider">Welcome Showcase</div>
      </div>

      {/* Showcase items */}
      <div className="flex flex-wrap gap-2">
        {sorted.map((carousel) => {
          const isActive = carousel.id === activeId;
          const isEditing = carousel.id === editingId;
          const published = isPublished(carousel);
          const isPublishing = publishingId === carousel.id;

          return (
            <button
              key={carousel.id}
              onClick={() => !isEditing && onSelect(carousel.id)}
              className={cn(
                'group carousel-item',
                isActive ? 'carousel-item-active' : 'carousel-item-inactive',
                pendingDeleteId === carousel.id && 'carousel-item-pending-delete',
              )}
            >
              {/* LED — amber for published, green for active draft */}
              <span
                className="carousel-led"
                style={published ? {
                  background: '#e8b84b',
                  boxShadow: '0 0 4px 1px rgba(232, 184, 75, 0.4)',
                } : undefined}
              />

              {/* Delete badge — hidden for published carousels */}
              {!isEditing && !published && pendingDeleteId !== carousel.id && (
                <span
                  onClick={(e) => handleDeleteClick(carousel.id, e)}
                  className="corner-badge corner-badge-tr corner-badge-sm corner-badge-delete corner-badge-hover"
                  title="Delete showcase"
                  role="button"
                  aria-label={`Delete ${carousel.name}`}
                >
                  <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </span>
              )}

              {/* Publish badge — top-left corner */}
              {!isEditing && (
                <span
                  onClick={(e) => handlePublishToggle(carousel, e)}
                  className={cn(
                    'corner-badge corner-badge-tl corner-badge-sm corner-badge-hover',
                    'transition-colors',
                    published
                      ? 'text-amber-400 hover:text-amber-300'
                      : 'text-white/30 hover:text-white/60',
                  )}
                  title={published ? 'Published — click to unpublish' : 'Click to publish'}
                  role="button"
                  aria-label={published ? 'Unpublish' : 'Publish'}
                  style={{ left: '2px', top: '2px' }}
                >
                  {isPublishing ? (
                    <span className="animate-spin inline-block h-2 w-2 border border-current border-t-transparent rounded-full" />
                  ) : (
                    <svg width="8" height="8" viewBox="0 0 24 24" fill={published ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  )}
                </span>
              )}

              {isEditing ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={handleFinishRename}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-transparent border-none outline-none w-36 text-[11px] font-bold text-white/90"
                  autoFocus
                />
              ) : (
                <span
                  onDoubleClick={(e) => handleStartRename(carousel, e)}
                  className="max-w-[140px] truncate"
                >
                  {carousel.name}
                </span>
              )}
            </button>
          );
        })}

        {/* Add showcase button */}
        <button
          onClick={onCreate}
          disabled={isCreating}
          className="carousel-item carousel-item-inactive"
          title="New showcase"
          style={{ gap: '4px' }}
        >
          {isCreating ? (
            <span className="animate-spin h-3 w-3 border-[1.5px] border-white/20 border-t-white/60 rounded-full" />
          ) : (
            <IconPlus className="h-3 w-3" style={{ opacity: 0.5 }} />
          )}
          <span>{isCreating ? 'Creating…' : 'New'}</span>
        </button>
      </div>

      {/* Inline delete confirmation */}
      {pendingDeleteId && pendingCarousel && (
        <div
          className="absolute z-20 w-[280px] bg-[#1a1214] border border-red-500/15 rounded-lg px-2.5 py-2.5 shadow-lg shadow-black/40"
          style={{ top: popupPos.top, left: popupPos.left }}
        >
          <div className="flex items-start gap-2 text-[11px] text-red-400/80">
            <svg className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>
              Delete <strong>{pendingCarousel.name}</strong>? This cannot be undone.
            </span>
          </div>
          <div className="flex gap-2 mt-2.5 ml-5">
            <button
              onClick={handleDeleteConfirm}
              className="px-3 py-1 text-[11px] font-medium rounded-md bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
            >
              Delete
            </button>
            <button
              onClick={handleDeleteCancel}
              className="px-3 py-1 text-[11px] font-medium rounded-md bg-white/[0.04] text-white/50 hover:bg-white/[0.08] hover:text-white/70 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-1.5 text-[10px] text-white/30">
        {sorted.some(isPublished)
          ? '★ Published showcase is shown to all visitors'
          : 'Publish a showcase to make it the default for visitors'}
      </div>
    </div>
  );
}
